import '../styles/NavigationDropdownMenu.css';
import { array, number, oneOfType, shape, string } from 'prop-types';
import { FaChevronDown } from 'react-icons/fa';
import Nav from 'react-bootstrap/Nav';
import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';


/** CONFIGURATION */
/**
 * @constant {number} DEFAULT_MAX_CHILD_ITEMS
 * The default maximum number of child items to display when the
 * `maxChildItemsToDisplay` property is not set or is zero.
 */
const DEFAULT_MAX_CHILD_ITEMS = 8;
/**
 * @constant {boolean} USE_DEFAULT_LIMIT
 * Determines whether to use the default limit for the number of child items to
 * display or display all children when the `maxChildItemsToDisplay` property
 * is not set or is zero.
 */
const USE_DEFAULT_LIMIT = true;

const NavigationDropdownMenu = forwardRef(({navLink}, ref) => {
    const contentRef = useRef(null);
    const toggleRef = useRef(null);
    const [displayContent, setDisplayContent] = useState(false);

    const menuItems = useMemo(() => {
        const { children } = navLink;
        const menuItemCount = children?.length ?? 0;
        if (menuItemCount === 0) {
            return [];
        }
        const { maxChildItemsToDisplay } = navLink;
        if (typeof maxChildItemsToDisplay === 'undefined'
            || maxChildItemsToDisplay === null
            || maxChildItemsToDisplay === 0) {
            return USE_DEFAULT_LIMIT === true
                ? children.slice(0, DEFAULT_MAX_CHILD_ITEMS)
                : children;
        }
        return children.slice(0, maxChildItemsToDisplay);
    });

    const onClose = useCallback(() => {
        setDisplayContent(false);
    }, []);

    const handleMouseOver = useCallback((e) => {
        if (!e.target.classList.contains('nav-dropdown-menu--content')
            && (contentRef.current.contains(e.target)
                || toggleRef.current.contains(e.target)
            )
        ) {
            setDisplayContent(true);
        } else {
            setDisplayContent(false);
        }
    }, []);

    const onChildNavLinkClick = useCallback(() => setDisplayContent(false), []);

    return (<button
        className={'nav-dropdown-menu--container'}
        ref={ref}
        onMouseOut={onClose}
        onBlur={onClose}
        onMouseOver={handleMouseOver}
        onFocus={handleMouseOver}>
        <Nav.Link
            as={Link}
            ref={toggleRef}
            className={'nav-link'}
            to={navLink.url}>
            {navLink.title}
            {navLink.children?.length > 0 && <FaChevronDown
                size={'10px'} />}
        </Nav.Link>
        <div
            className={'nav-dropdown-menu--content'}
            style={{ transform: displayContent ? 'scaleY(1)': 'scaleY(0)' }}
            ref={contentRef}>
            {menuItems.length > 0 && <ul
                className={'nav-dropdown-menu--content-main'}>
                <div
                    className={'nav-dropdown-menu--content-items'}>
                    {menuItems.map((child) => (
                        <li
                            key={child.id}>
                            <Link
                                onClick={onChildNavLinkClick}
                                className={'child-nav-link'}
                                to={child.url}>
                                <div
                                    className={'navbar--text-container'}
                                >
                                    {child.title}
                                </div>
                            </Link>
                        </li>
                    ))}
                </div>
            </ul>}
        </div>
    </button>);
});

NavigationDropdownMenu.defaultProps = {};
NavigationDropdownMenu.displayName = 'NavigationDropdownMenu';
NavigationDropdownMenu.propTypes = {
    navLink: shape({
        children: array,
        id: oneOfType([number, string]),
        title: string,
    }),
};

export default NavigationDropdownMenu;